import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { FormikProps } from "formik";
import Grid2 from "@mui/material/Grid2"; // استفاده از Grid2
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  API_URL,
  handleSendMessage,
  remainingSeasonsInPersianYear,
} from "src/utilities";
import { useAuth } from "src/context/auth";
import { DatePicker } from "@mui/x-date-pickers";
import AgreementService from "./agreementServices";
import { IAgreementService } from "src/interface/IAgreementService";
import { IService } from "src/interface/IService";
import { useNotifications } from "@toolpad/core/useNotifications";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import { IBaseAgreement } from "src/interface/IBaseAgreements";
import moment from "jalali-moment";
import { IBaseAgreementItem } from "src/interface/IBaseAgreementItem";
import { BaseAgreeType } from "src/interface/enum/BaseAgreeType";
import { IAgreement } from "src/interface/IAgreement";
import { getRoutes } from "src/utilities/routes";
import SkeltonProgress from "src/components/SkeltonProgress";

// ساخت schema برای اعتبارسنجی با yup
interface AgreementFormProps {
  formik: FormikProps<IAgreement>;
  agreementServices: IAgreementService[];
  setAgreementServices: (agreementServices: IAgreementService[]) => void;
  customers: IAutoCompleteLabelValue[];
  existingAgreementServices?: IAgreementService[];
  isEdit?: boolean;
  existingServiceLoading?: boolean;
  baseAgreements: IBaseAgreement[];
  setBaseAgreements: (baseAgreements: IBaseAgreement[]) => void;
}

const AgreementForm = ({
  formik,
  agreementServices,
  setAgreementServices,
  customers,
  isEdit,
  existingServiceLoading = false,
  existingAgreementServices,
  baseAgreements,
  setBaseAgreements,
}: AgreementFormProps) => {
  const { user } = useAuth();
  const Routes = getRoutes();
  useEffect(() => {
    if (formik.values.startedAt)
      formik.setFieldValue(
        "sessionCounter",
        remainingSeasonsInPersianYear(new Date(formik.values.startedAt))
      );
  }, [formik.values.startedAt]);

  const { mutateAsync: getBaseAgreements, isPending: isPendingBaseAgreements } =
    useMutation({
      mutationFn: async () => {
        const response = await fetch(Routes.BaseAgreementsRoutes, {
          method: "GET",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${user?.token}`,
          },
        });

        return await response.json();
      },
      onSuccess: (responseData: IBaseAgreement[]) => {
        setBaseAgreements(responseData);
      },
      onError: (error) => {
        console.log(error);
      },
    });

  useEffect(() => {
    user?.token && getBaseAgreements();
  }, [user?.token]);

  const [currentActivities, setCurrentActivities] = useState<
    IAutoCompleteLabelValue[]
  >([]);

  const [currentServices, setCurrentServices] = useState<
    IAutoCompleteLabelValue[]
  >([]);
  useEffect(() => {
    if (baseAgreements.length > 0 && formik.values.startedAt) {
      const currentYear =
        formik.values.startedAt &&
        moment(formik.values.startedAt).locale("fa").format("YYYY");

      const currentYearBase = baseAgreements?.find(
        (b) => b.name === currentYear
      );

      if (
        currentYearBase &&
        currentYearBase.baseAgreementItems &&
        currentYearBase.baseAgreementItems.length > 0
      ) {
        setCurrentActivities(
          currentYearBase?.baseAgreementItems
            ?.filter((i) => i.typeItem === BaseAgreeType.TypeActivity)
            ?.map((item: IBaseAgreementItem) => ({
              label: item.title ?? "",
              value: item.id,
              item: item,
            }))
        );
        setCurrentServices(
          currentYearBase?.baseAgreementItems
            ?.filter((i) => i.typeItem === BaseAgreeType.TypeService)
            .map((item: IBaseAgreementItem) => ({
              label: item.title ?? "",
              value: item.id,
              item: item,
            }))
        );
      }
    }
  }, [
    baseAgreements,
    formik.values.baseAgreementActivityRef,
    formik.values.baseAgreementServiceRef,
    formik.values.startedAt,
  ]);

  const { data: ServicesData, isPending: isPendingServicesData } = useQuery<
    IService[]
  >({
    queryKey: ["ServicesData"],
    queryFn: () =>
      fetch(API_URL + "services", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      }).then((res) => res.json()),
  });

  const notifications = useNotifications();
  useEffect(() => {
    const services = ServicesData ?? [];
    let factor = 0;
    let price = 0;
    agreementServices.map((row) => {
      if (row.selected) {
        if (
          row.service?.followUpGroups &&
          row.service?.followUpGroups?.length > 0
        ) {
          if (row.followUpGroupRef === 0 || !row.followUpGroupRef) {
            handleSendMessage(
              notifications,
              "برای '" + row.service.name + "' یک 'گروه پیگیری' انتخاب کنید",
              "error",
              2000,
              row.service?.id?.toString()
            );
          }
        }
        if (!row.serviceItemByTypeRef) {
          handleSendMessage(
            notifications,
            "برای '" + row.service?.name + "' یک 'نوع' انتخاب کنید",
            "error",
            2000,
            row.service?.id?.toString()
          );
        }
        if (!row.serviceItemByVolumeRef) {
          handleSendMessage(
            notifications,
            "برای '" + row.service?.name + "' یک 'حجم' انتخاب کنید",
            "error",
            2000,
            row.service?.id + "(1)"?.toString()
          );
        }

        const service = services.find((s) => s.id === row.serviceRef);
        const serviceTypeItem = service?.serviceItems?.find(
          (i) => i.id === row.serviceItemByTypeRef
        );
        const serviceVolumeItem = service?.serviceItems?.find(
          (i) => i.id === row.serviceItemByVolumeRef
        );

        price +=
          (service?.price ?? 0) *
          (row?.count ?? 0) *
          (serviceTypeItem?.factor ?? 0) *
          (serviceVolumeItem?.factor ?? 0);
      }
    });
    const totalPrice =
      price *
      (formik.values.delayFactor
        ? parseFloat(formik.values.delayFactor?.toString())
        : 0);

    const baseAgreementPrice =
      (parseInt(
        formik.values.baseAgreementService?.value?.toString() ?? "0",
        10
      ) /
        4) *
      parseInt((formik.values.sessionCounter ?? "0").toString(), 10);

    const servicePrice =
      baseAgreementPrice *
      parseFloat(
        formik.values.baseAgreementActivity?.value?.toString() ?? "1"
      ) *
      (formik.values.delayFactor
        ? parseFloat(formik.values.delayFactor?.toString())
        : 0);

    if (totalPrice > servicePrice) formik.setFieldValue("price", totalPrice);
    else formik.setFieldValue("price", servicePrice);
  }, [
    agreementServices,
    formik.values.delayFactor,
    formik.values.baseAgreementService,
    formik.values.baseAgreementActivity,
  ]);

  if (
    existingServiceLoading ||
    isPendingServicesData ||
    isPendingBaseAgreements
  ) {
    return <SkeltonProgress />;
  }
  console.log("formik.values.customerRef", formik.values.customerRef);

  return (
    <>
      <Grid2 container spacing={1} marginY={1} columns={12}>
        <Grid2 size={3}>
          <Autocomplete
            value={
              customers.find(
                (customer) => customer.value === formik.values.customerRef
              ) || null // null به جای undefined
            }
            onChange={(event, newValue) => {
              formik.setFieldValue(
                "customerRef",
                newValue ? newValue.value : null
              ); // مدیریت مقدار null
            }}
            options={customers || []}
            getOptionLabel={(option) => option.label || ""} // نمایش لیبل گزینه‌ها
            isOptionEqualToValue={(option, value) =>
              option.value === value?.value
            } // مقایسه درست گزینه‌ها
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              // استفاده از value به عنوان key برای جلوگیری از تکراری (value باید unique باشد)
              // اگر value null است، از ترکیب label و key استفاده می‌کنیم
              const uniqueKey =
                option.value != null
                  ? String(option.value)
                  : `${option.label || ""}_${key || ""}`;
              return (
                <li {...otherProps} key={uniqueKey}>
                  {option.label || ""}
                </li>
              );
            }}
            fullWidth
            renderInput={(params) => (
              <TextField
                error={
                  formik.touched.customerRef &&
                  Boolean(formik.errors.customerRef)
                }
                helperText={
                  formik.touched.customerRef && formik.errors.customerRef
                    ? formik.errors.customerRef
                    : ""
                }
                {...params}
                label="مشتری"
                placeholder="مشتری"
              />
            )}
          />
        </Grid2>
        <Grid2 size={2}>
          <DatePicker
            label="تاریخ شروع"
            value={
              formik.values.startedAt instanceof Date
                ? formik.values.startedAt
                : formik.values.startedAt
                  ? new Date(formik.values.startedAt)
                  : null
            }
            onChange={(date: Date | null) =>
              formik.setFieldValue("startedAt", date)
            }
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error:
                  formik.touched.startedAt && Boolean(formik.errors.startedAt),
                helperText: formik.touched.startedAt && formik.errors.startedAt,
              },
            }}
          />
        </Grid2>
        <Grid2 size={1}>
          <TextField
            {...formik.getFieldProps("sessionCounter")} // استفاده از ویژگی‌های فرمیک
            label="َتعداد فصل"
            type="number"
            error={
              formik.touched.sessionCounter &&
              Boolean(formik.errors.sessionCounter)
            }
            helperText={
              (formik.touched.sessionCounter &&
                formik.errors.sessionCounter) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={1}>
          <TextField
            {...formik.getFieldProps("delayFactor")} // استفاده از ویژگی‌های فرمیک
            label="َضریب تاخیر"
            type="number"
            error={
              formik.touched.delayFactor && Boolean(formik.errors.delayFactor)
            }
            helperText={
              (formik.touched.delayFactor &&
                formik.errors.delayFactor) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={2}>
          <TextField
            value={formik.values.price?.toLocaleString()}
            label="مبلغ"
            contentEditable={false}
            error={formik.touched.price && Boolean(formik.errors.price)}
            helperText={
              (formik.touched.price && formik.errors.price) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>
        <Grid2 size={1}>
          <Autocomplete
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={
              currentServices.length > 0
                ? currentServices.find(
                    (editor: any) =>
                      editor.value === formik.values.baseAgreementServiceRef
                  )
                : null // مقدار پیش‌فرض معتبر
            }
            onChange={(event: any, newValue: any) => {
              formik.setFieldValue(
                "baseAgreementServiceRef",
                newValue?.value || undefined
              ); // اطمینان از مقدار مناسب

              formik.setFieldValue(
                "baseAgreementService",
                currentServices.find(
                  (editor: any) => editor.value === newValue?.value
                )?.item
              );
            }}
            options={currentServices || []}
            fullWidth
            renderInput={(params) => (
              <TextField
                error={
                  formik.touched.baseAgreementServiceRef &&
                  Boolean(formik.errors.baseAgreementServiceRef)
                }
                helperText={
                  formik.touched.baseAgreementServiceRef &&
                  formik.errors.baseAgreementServiceRef
                    ? (formik.errors.baseAgreementServiceRef as string)
                    : ""
                }
                {...params}
                label="نوع شخصیت"
                placeholder="نوع شخصیت"
              />
            )}
          />
        </Grid2>
        <Grid2 size={2}>
          <Autocomplete
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={
              currentActivities?.length > 0
                ? currentActivities.find(
                    (editor) =>
                      editor.value === formik.values.baseAgreementActivityRef
                  )
                : null // مقدار پیش‌فرض معتبر
            }
            onChange={(event: any, newValue: any) => {
              formik.setFieldValue(
                "baseAgreementActivityRef",
                newValue?.value || undefined
              ); // اطمینان از مقدار مناسب
              formik.setFieldValue(
                "baseAgreementActivity",
                currentActivities.find(
                  (editor: any) => editor.value === newValue?.value
                )?.item
              );
            }}
            options={currentActivities || []}
            fullWidth
            renderInput={(params) => (
              <TextField
                error={
                  formik.touched.baseAgreementActivityRef &&
                  Boolean(formik.errors.baseAgreementActivityRef)
                }
                helperText={
                  formik.touched.baseAgreementActivityRef &&
                  formik.errors.baseAgreementActivityRef
                    ? (formik.errors.baseAgreementActivityRef as string)
                    : ""
                }
                {...params}
                label="نوع فعالیت"
                placeholder="نوع فعالیت"
              />
            )}
          />
        </Grid2>
      </Grid2>

      <AgreementService
        isEdit={isEdit}
        existingAgreementServices={existingAgreementServices}
        formik={formik}
        rows={agreementServices}
        ServicesData={ServicesData ?? []}
        setRows={setAgreementServices}
      />
    </>
  );
};

export default AgreementForm;
